import { Button, Card, Checkbox, Col, Divider, Flex, Image, Modal, Radio, RadioChangeEvent, Row, Table, TableProps, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// components
import FilePreview from 'components/filePreview/filePreview';
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';

// services

// styles

// models
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { isRTL } from 'core/services/language';
import { formatFileSize } from 'utils/FileSize';
import DisabledElement from '../../../../../components/disabledElement/disabledElement';
import { onCancelDividend, onGetDividendById, onPreviewDividend } from '../services/dividends';
import { Dividend, DividendPreviewShareholder } from '../models/dividends';
import StatusComponent from 'components/status/status';
import { StatusColors } from 'utils/statusColors';
import Icon from 'utils/icon';
import { dateFormat } from 'utils/dateFormat';
import NoData from 'components/noData/noData';
import { TranslatedField } from 'core/models/common';
import { SadadPayment, VerificationPayload } from '../../commonModels';
import SadadInvoiceComponent from '../../sadadInvoice';
import VerificationModal from '../../verificationModal';
import TableTotal from 'components/tableTotal/tableTotal';

interface DividendViewPageProps {}

const DividendViewPage: FunctionComponent<DividendViewPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [dividendDetails, setDividendDetails] = useState<Dividend>();
	const [payments, setPayments] = useState<SadadPayment[]>();

	const [beneficiaries, setBeneficiaries] = useState<DividendPreviewShareholder[]>();
	const [beneficiariesCurrentPage, setBeneficiariesCurrentPage] = useState<number>(1);
	const [totalBeneficiaries, setTotalBeneficiaries] = useState<number>(0);
	const [beneficiariesModalOpen, setBeneficiariesModalOpen] = useState<boolean>(false);
	const [isBeneficiariesLoading, setIsBeneficiariesLoading] = useState<boolean>(false);

	const [isCancelling, setIsCancelling] = useState<boolean>(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	const absteensColumns: TableProps['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: t('COMMON.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name: TranslatedField) => (isRTL() ? name.ar : (name.en ?? name.ar)),
		},
		{
			title: t('ACTIONS.PROFIT'),
			dataIndex: 'amount',
			key: 'amount',
		},
	];

	const directIbanColumns: TableProps['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: t('COMMON.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name: TranslatedField) => (isRTL() ? name.ar : (name.en ?? name.ar)),
		},
		{
			title: t('ACTIONS.PROFIT'),
			dataIndex: 'amount',
			key: 'amount',
		},
		{
			title: t('ACTIONS.IBAN'),
			dataIndex: 'iban',
			key: 'iban',
		},
	];

	const beneficiariesColumns: TableProps['columns'] = [
		{
			title: t('COMMON.NATIONAL_ID'),
			dataIndex: 'national_id',
			key: 'national_id',
		},
		{
			title: t('COMMON.NAME'),
			dataIndex: 'shareholder_name',
			key: 'shareholder_name',
		},
		{
			title: t('COMMON.SHARES'),
			dataIndex: 'shares',
			key: 'shares',
			render: (shares: number) => shares?.toLocaleString(),
		},
		{
			title: t('ACTIONS.TRANSFER_TYPE'),
			dataIndex: 'translated_transfer_type',
			key: 'translated_transfer_type',
			render: (field: TranslatedField) => (isRTL() ? field?.ar : field?.en),
		},
	];

	useEffect(() => {
		getDividendRequest();
	}, []);

	useEffect(() => {
		if (beneficiariesModalOpen) getDividendPreview();
		else {
			setBeneficiaries([]);
			setBeneficiariesCurrentPage(1);
			setTotalBeneficiaries(0);
		}
	}, [beneficiariesModalOpen]);

	const getDividendPreview = async (page = 1) => {
		try {
			setIsBeneficiariesLoading(true);

			const response = await onPreviewDividend(id as string, page);
			setBeneficiaries(response.shareholders);
			setTotalBeneficiaries(response.pagination.total);
			setBeneficiariesCurrentPage(page);

			setIsBeneficiariesLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsBeneficiariesLoading(false);
			setBeneficiariesModalOpen(false);
		}
	};

	const getDividendRequest = async () => {
		try {
			setIsLoading(true);

			const response = await onGetDividendById(id as string);
			setDividendDetails(response.action);
			setPayments(response.payments);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/update-documents');
		}
	};

	const cancelHandler = async (payload: VerificationPayload) => {
		try {
			setIsCancelling(true);

			await onCancelDividend(Number(id), payload.key);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.DIVIDEND_CANCELLED_SUCCESS'));

			setVerificationModalOpen(false);
			setIsCancelling(false);
			getDividendRequest();
		} catch (error) {
			setIsCancelling(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.DIVIDEND_NOT_CANCELLED'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Card>
					<Flex justify="space-between">
						<div>
							<Flex align="center" gap={20} className="mb-2">
								<h5 className="mb-0 fw-medium">{dividendDetails?.formated_request_number}</h5>
								<StatusComponent
									small
									text={t(`COMMON.${dividendDetails?.status_key.toUpperCase()}`)}
									color={StatusColors[dividendDetails?.status_key!]}
								/>
							</Flex>

							<p className="text-light">{dividendDetails?.request_date}</p>
						</div>

						{dividendDetails?.status_key !== 'Canceled' && (
							<Flex gap={10}>
								<Button type="primary" size="middle" className="px-3" onClick={() => setBeneficiariesModalOpen(true)}>
									{t('ACTIONS.BENEFICIARIES')}
								</Button>

								<Button type="primary" size="middle" danger className="px-3" onClick={() => setVerificationModalOpen(true)}>
									{t('COMMON.CANCEL')}
								</Button>
							</Flex>
						)}
					</Flex>

					<h6 className="fw-medium">{t(`ACTIONS.${dividendDetails?.interval_of_distribution_key.toLocaleUpperCase()}_DIVIDENDS`)}</h6>

					<Row gutter={[16, 16]} className="my-4">
						<Col xs={24} md={12} lg={6}>
							<DisabledElement showPrice value={dividendDetails?.revenue_per_share} label={t('ACTIONS.REVENUE_PER_SHARE')} />
						</Col>
						<Col xs={24} md={12} lg={6}>
							<DisabledElement showPrice value={dividendDetails?.total_amount_of_dividends} label={t('ACTIONS.TOTAL_AMOUNT_OF_DIVIDENDS')} />
						</Col>
						<Col xs={24} md={12} lg={6}>
							<DisabledElement value={dateFormat(dividendDetails?.eligibility_date!, true)} label={t('ACTIONS.ELIGIBILITY_DATE')} />
						</Col>
						<Col xs={24} md={12} lg={6}>
							<DisabledElement value={dateFormat(dividendDetails?.distribution_date!, true)} label={t('ACTIONS.DISTRIBUTION_DATE')} />
						</Col>
					</Row>
					<Divider />

					<div>
						<Typography.Text style={{ fontSize: 15, marginBottom: 5, display: 'inline-block', fontWeight: 500 }}>
							{t('ACTIONS.ABSTEENED_SHAREHOLDERS')}
						</Typography.Text>

						<Table
							columns={absteensColumns}
							dataSource={dividendDetails?.forbidden_excepted_shareholders}
							bordered
							rowKey="id"
							pagination={false}
							locale={{ emptyText: <NoData /> }}
						/>
					</div>
					<Divider />

					<div>
						<Typography.Text style={{ fontSize: 15, marginBottom: 5, display: 'inline-block', fontWeight: 500 }}>
							{t('ACTIONS.DIRECT_TRANSFER_TO_IBAN')}
						</Typography.Text>

						<Table
							columns={directIbanColumns}
							dataSource={dividendDetails?.direct_transfer_excepted_shareholders}
							bordered
							rowKey="id"
							pagination={false}
							locale={{ emptyText: <NoData /> }}
						/>
					</div>

					<Divider />

					<Card>
						{payments?.map((payment, index) => (
							<SadadInvoiceComponent key={index} payment={payment} />
						))}
					</Card>

					{dividendDetails?.shareholders_dividends_list?.title && (
						<>
							<Divider />

							<Card>
								<h6 className="fw-medium">{t('ACTIONS.SHAREHOLDERS_DIVIDENDS_LIST')}</h6>

								<FilePreview
									name={dividendDetails?.shareholders_dividends_list?.title}
									downloadLink={dividendDetails?.shareholders_dividends_list?.download_link!}
									data={formatFileSize(dividendDetails?.shareholders_dividends_list?.size_in_kilobyte!)}
									style={{ background: '#fff' }}
								/>
							</Card>
						</>
					)}
				</Card>
			)}

			<Modal
				open={beneficiariesModalOpen}
				title={t('ACTIONS.BENEFICIARIES')}
				onCancel={() => setBeneficiariesModalOpen(false)}
				footer={null}
				width={800}>
				<Table
					columns={beneficiariesColumns}
					dataSource={beneficiaries}
					className="bordered"
					scroll={{ x: 600 }}
					rowKey="national_id"
					loading={isBeneficiariesLoading}
					pagination={{
						pageSize: 15,
						total: totalBeneficiaries,
						showSizeChanger: false,
						current: beneficiariesCurrentPage,
						onChange: getDividendPreview,
						showTotal: (total) => <TableTotal total={total} />,
					}}
					locale={{ emptyText: <NoData /> }}
				/>
			</Modal>

			<VerificationModal
				title={
					dividendDetails?.status_key === 'PendingPayment'
						? t('ACTIONS.CONFIRM_CANCEL_PENDING_DIVIDEND')
						: t('ACTIONS.CONFIRM_CANCEL_SCHEDULED_DIVIDEND')
				}
				subTitle={t('ACTIONS.PLEASE_CONFIRM_YOUR_REQUEST')}
				otpOnly
				open={verificationModalOpen}
				isLoading={isCancelling}
				className="confirmationModal"
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={cancelHandler}
				module="dividends_cancel"
				note={t('ACTIONS.DIVIDEND_CANCEL_NOTE')}
				noteType="red"
			/>
		</>
	);
};

export default DividendViewPage;
